import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import FilterBar from '../components/FilterBar';
import GarmentGrid from '../components/GarmentGrid';
import GarmentModal from '../components/GarmentModal';
import Footer from '../components/Footer';
import { Plus, ArrowLeft } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/sonner';
import { isAdminMode } from '../utils/adminMode';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const sectionConfig = {
  mujer: {
    title: 'Women',
    subtitle: 'Collections',
    description: 'Diseñada para mujeres que buscan comodidad, estilo y fuerza interior.',
    gender: 'MUJER',
  },
  hombre: {
    title: 'Men',
    subtitle: 'Collections',
    description: 'Ropa deportiva pensada para hombres que buscan rendimiento y estilo.',
    gender: 'HOMBRE',
  },
  accesorios: {
    title: 'Accessories',
    subtitle: 'Complete your style',
    description: 'Los accesorios perfectos para completar tu look deportivo.',
    gender: 'ACCESORIOS',
  },
  rebajas: {
    title: 'Sale',
    subtitle: 'Rebajas',
    description: 'Precios especiales por tiempo limitado. Aprovecha estas rebajas.',
    gender: null,
    saleOnly: true,
  },
};

const SectionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const section = location.pathname.replace('/', '') || 'mujer';
  const config = sectionConfig[section] || sectionConfig.mujer;

  // Leer ?collection=XXX de la URL al montar (usado al venir de las cards de la landing)
  const initialCollection = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    const col = params.get('collection');
    if (col) return col.toUpperCase().startsWith('COLLECTION') ? col.toUpperCase() : `COLLECTION ${col.toUpperCase()}`;
    return 'all';
  }, [location.search]);

  const [garments, setGarments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    gender: 'all',
    category: 'all',
    search: '',
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedGarment, setSelectedGarment] = useState(null);
  const [selectedCollection, setSelectedCollection] = useState(initialCollection);
  const { toast } = useToast();

  const [loadError, setLoadError] = useState(false);

  const fetchGarments = async () => {
    setLoading(true);
    setLoadError(false);
    const params = config.gender ? { gender: config.gender } : {};
    try {
      const response = await axios.get(`${API}/garments`, { params, timeout: 20000 });
      setGarments(response.data);
    } catch (error) {
      setLoadError(true);
    }
    setLoading(false);
  };

  const initializeDatabase = async () => {
    try {
      await axios.post(`${API}/garments/init/collection-dreams`);
      toast({ title: 'Éxito', description: 'Collection inicializada' });
      fetchGarments();
    } catch (error) {
      console.error('Error initializing:', error);
    }
  };

  useEffect(() => {
    fetchGarments();
  }, [section]);

  // Sincronizar collection cuando cambia la URL (?collection=LOTUS/TULIPS)
  useEffect(() => {
    setSelectedCollection(initialCollection);
  }, [initialCollection]);

  const filteredGarments = useMemo(() => {
    return garments.filter((garment) => {
      const matchesCategory =
        !filters.category || filters.category === 'all' || garment.category === filters.category;
      const matchesSearch =
        !filters.search || garment.name.toLowerCase().includes(filters.search.toLowerCase());
      const matchesCollection =
        selectedCollection === 'all' ||
        (garment.collection || '').trim().toUpperCase() === selectedCollection.trim().toUpperCase();
      const matchesSale =
        !config.saleOnly ||
        (garment.sale_price != null && garment.price != null && garment.sale_price < garment.price);
      return matchesCategory && matchesSearch && matchesCollection && matchesSale;
    });
  }, [garments, filters, selectedCollection, config.saleOnly]);

  const availableCollections = useMemo(() => {
    // Sacar colecciones únicas presentes en las prendas actuales
    const byKey = new Map();
    garments.forEach((g) => { const c = (g.collection || '').trim(); if (c && !byKey.has(c.toUpperCase())) byKey.set(c.toUpperCase(), c); });
    const collections = [...byKey.values()];
    return collections;
  }, [garments]);

  const categories = useMemo(() => {
    return [...new Set(garments.map((g) => g.category))];
  }, [garments]);

  const handleCreate = () => {
    setSelectedGarment(null);
    setIsModalOpen(true);
  };

  const handleEdit = (garment) => {
    setSelectedGarment(garment);
    setIsModalOpen(true);
  };

  const handleDelete = async (garmentId) => {
    try {
      await axios.delete(`${API}/garments/${garmentId}`);
      toast({ title: 'Éxito', description: 'Prenda eliminada correctamente' });
      fetchGarments();
    } catch (error) {
      console.error('Error deleting garment:', error);
      toast({
        title: 'Error',
        description: 'No se pudo eliminar la prenda',
        variant: 'destructive',
      });
    }
  };

  const handleSave = () => {
    setIsModalOpen(false);
    fetchGarments();
  };

  return (
    <div className="min-h-screen bg-white text-black">
      <Header />

      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        {/* Back link */}
        <button
          onClick={() => navigate('/')}
          className="text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-500 hover:text-black transition-colors flex items-center gap-1.5 mb-6"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} />
          Inicio
        </button>

        {/* Editorial Header */}
        <div className="mb-8 sm:mb-10">
          <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-neutral-500 mb-3">
            {config.subtitle}
          </p>
          <h1 className="text-5xl sm:text-7xl lg:text-8xl font-black uppercase tracking-tighter leading-none mb-4">
            {config.title}
          </h1>
          <p className="text-sm text-neutral-600 max-w-xl leading-relaxed">
            {config.description}
          </p>
        </div>

        {/* Collection Selector — premium boutique style */}
        {availableCollections.length > 1 && (
          <div className="mb-10 sm:mb-14" data-testid="collection-selector">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-500 mb-4">
              Elige tu colección
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCollection('all')}
                className={`px-6 sm:px-8 py-3.5 text-[11px] font-bold uppercase tracking-[0.2em] transition-all border ${
                  selectedCollection === 'all'
                    ? 'bg-black text-white border-black'
                    : 'bg-white text-black border-neutral-300 hover:border-black'
                }`}
                data-testid="collection-all"
              >
                Todas
              </button>
              {availableCollections.map((coll) => {
                const label = coll.replace(/^COLLECTION\s+/i, '').trim();
                const isActive = selectedCollection.toUpperCase() === coll.toUpperCase();
                return (
                  <button
                    key={coll}
                    onClick={() => setSelectedCollection(coll)}
                    className={`px-6 sm:px-8 py-3.5 text-[11px] font-bold uppercase tracking-[0.2em] transition-all border ${
                      isActive
                        ? 'bg-black text-white border-black'
                        : 'bg-white text-black border-neutral-300 hover:border-black'
                    }`}
                    data-testid={`collection-${label.toLowerCase()}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Admin: Add button */}
        {isAdminMode() && (
          <div className="flex justify-end mb-4">
            <button
              onClick={handleCreate}
              className="bg-black text-white text-xs font-bold uppercase tracking-[0.2em] px-6 py-3 hover:bg-neutral-800 transition-colors flex items-center gap-2"
              data-testid="add-garment-button"
            >
              <Plus className="w-4 h-4" strokeWidth={2} />
              Añadir prenda
            </button>
          </div>
        )}

        {/* Filters */}
        <FilterBar
          filters={filters}
          setFilters={setFilters}
          categories={categories}
          hideGenderFilter={true}
        />

        {/* Results Count */}
        <div className="mb-8">
          <p
            className="text-[11px] font-bold uppercase tracking-[0.3em] text-neutral-500"
            data-testid="results-count"
          >
            {filteredGarments.length} {filteredGarments.length === 1 ? 'prenda' : 'prendas'}
          </p>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-24">
            <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : loadError ? (
          <div className="text-center py-24 border-y border-neutral-200" data-testid="garments-load-error">
            <p className="text-neutral-500 text-sm mb-4">No hemos podido cargar las prendas. Comprueba tu conexión.</p>
            <button
              onClick={fetchGarments}
              className="text-xs font-bold uppercase tracking-[0.2em] border border-black px-6 py-3 hover:bg-black hover:text-white transition-colors"
              data-testid="garments-retry-btn"
            >
              Reintentar
            </button>
          </div>
        ) : filteredGarments.length === 0 ? (
          <div className="text-center py-24 border-y border-neutral-200">
            <p className="text-neutral-500 text-sm mb-4">No hay productos en esta sección todavía.</p>
            {isAdminMode() && garments.length === 0 && (
              <button
                onClick={initializeDatabase}
                className="text-xs font-bold uppercase tracking-[0.2em] border border-black px-6 py-3 hover:bg-black hover:text-white transition-colors"
              >
                Inicializar
              </button>
            )}
          </div>
        ) : (
          <GarmentGrid garments={filteredGarments} onEdit={handleEdit} onDelete={handleDelete} />
        )}
      </main>

      <GarmentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        garment={selectedGarment}
        categories={categories}
        defaultGender={config.gender}
      />

      <Toaster />
      <Footer />
    </div>
  );
};

export default SectionPage;
